import { For, Index, Show, type JSX } from "solid-js";

import { Icon } from "~/icon";

import styles from "../deck.module.css";
import { useDeckWords } from "../deck_words";
import type { TimelineSlide as TimelineSlideData } from "../types";

const MANY_STEPS = 4;

/** The visits as steps down the slide, each with its date (booked, or the plan's pencilled-in day), its work and chair
 *  time, and what happens before the next one: healing, or the lab making the new tooth. */
export function TimelineSlide(props: { slide: TimelineSlideData }): JSX.Element {
  const words = useDeckWords();
  return (
    <>
      <div class={styles["tl-head"]}>
        <h2 class={styles["s-h"]}>{props.slide.headline}</h2>
        <Show when={props.slide.minutes}>
          {(minutes) => (
            <p class={styles["tl-sum"]}>
              {words().t("inTheChairInAll", {
                visits: props.slide.steps.length,
                time: words().chairTime(minutes()),
              })}
            </p>
          )}
        </Show>
      </div>
      {/* Four visits or more fill two columns, down the first and then the second, so a long plan stays on the slide. */}
      <ol
        class={styles["tl"]}
        data-many={props.slide.steps.length >= MANY_STEPS ? "" : undefined}
        style={{ "--tl-rows": String(Math.ceil(props.slide.steps.length / 2)) }}
      >
        <Index each={props.slide.steps}>
          {(step) => (
            <li class={styles["tl-step"]}>
              <span class={styles["tl-n"]} aria-hidden="true">
                {step().number}
              </span>
              <div class={styles["tl-box"]}>
                <h3 class={styles["tl-t"]}>
                  {words().t("visitNumber", { number: step().number })}
                  <Show when={step().date}>
                    {(date) => (
                      <span class={styles["tl-d"]}>
                        {step().booked
                          ? words().dateWithWeekday(date())
                          : words().t("around", { date: words().dateWithWeekday(date()) })}
                      </span>
                    )}
                  </Show>
                </h3>
                <ul class={styles["vc-chips"]}>
                  <For each={step().items}>{(item) => <li>{item}</li>}</For>
                  <Show when={step().minutes}>
                    {(minutes) => <li class={styles["tl-min"]}>{words().chairTime(minutes())}</li>}
                  </Show>
                </ul>
              </div>
              <Show when={step().wait}>
                {(wait) => (
                  <p class={styles["tl-wait"]}>
                    <Icon name="clock" size="none" aria-hidden="true" />
                    <span>{wait()}</span>
                  </p>
                )}
              </Show>
            </li>
          )}
        </Index>
      </ol>
    </>
  );
}
